import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "utils/request";
import {
  getRetryPayload,
  registerRetryPayload,
  clearRetryPayload,
} from "./retryRegistry";

// Maps dropdown labels to backend endpoint segments (plural lowercase).
const ITEM_TYPE_TO_ENDPOINT = {
  Info: "infos",
  Issue: "issues",
  Overview: "overviews",
  Limit: "limits",
};

const initialState = {
  reportContent: {},
  // Full section/subsection structure from the report's template version
  // (GET /reports/{id}/all-sections/). Unlike reportContent.sections — which
  // only holds sections that currently have items — this includes empty ones,
  // so the Move-item modal can target every valid destination.
  allSections: [],
  requestStatus: "idle",
  error: null,
  newItemModal: {
    isOpen: false,
    type: null, // "Info" | "Issue" | "Overview" | "Limit"
    itemId: null,
  },
  aiProcessing: {
    items: [],
    isExpanded: true,
  },
  // Multiple items can be edited simultaneously. State is keyed by item id so
  // opening a second item never evicts the first. Drafts mirror each item's
  // editable fields locally so silent refetches don't clobber in-flight edits.
  edit: {
    // itemId → type ("Issue" etc.); presence marks an item as open in edit
    // mode. Mutated only on enter/exit, so list consumers reading this map
    // don't re-render on every keystroke.
    editingTypeById: {},
    // itemId → draft object. Updated per keystroke; each form reads only its
    // own entry.
    draftById: {},
    // itemId → bool. Published by each edit form so page-level navigation
    // guards can prompt before discarding. True when that item has changes.
    dirtyById: {},
  },
  // Map of item id → true for items whose photos have annotation changes that
  // haven't been fed back through AI yet. Drives the in-form indigo banner.
  pendingAnnotationRerun: {},
  loading: {
    createItem: false,
    deleteItem: false,
    aiGenerate: false,
    updateItem: false,
    compileReport: false,
    uploadCoverPhoto: false,
  },
  errors: {
    createItem: null,
    deleteItem: null,
    aiGenerate: null,
    updateItem: null,
    compileReport: null,
    uploadCoverPhoto: null,
  },
};

// Backend casing has shifted across endpoints — accept all variants.
const TERMINAL_TASK_STATES = new Set([
  "SUCCESS",
  "FAILURE",
  "TIMED-OUT",
  "TIMED_OUT",
  "TIMEOUT",
]);
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 120; // ~4 minutes safety cap

export const fetchStructuredReport = createAsyncThunk(
  "webReportEdit/fetchStructuredReport",
  async (reportId, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/reports/${reportId}/structured/`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch report");
    }
  }
);

// GET /reports/{id}/all-sections/ — the complete section/subsection structure
// from the report's template version (includes sections with no items yet).
// Powers the Move-item modal's destination dropdowns.
export const fetchAllSections = createAsyncThunk(
  "webReportEdit/fetchAllSections",
  async (reportId, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/reports/${reportId}/all-sections/`
      );
      return data?.sections || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch sections");
    }
  }
);

// POST /reports/{id}/compile/ — returns the compiled report id; caller
// opens compiled-reports/{id}/ in a new tab.
//
// `regenerate_summary`: dynamic. We ask the BE to regenerate only when the
// report has never had a summary (`summary_generated_at` is null). Otherwise
// the user's curated Inspection Overview is reused verbatim. BE only honors
// the literal "false" — any other value (typos included) falls back to
// regenerate, so default-string conversion via `String(bool)` is safe.
export const compileReport = createAsyncThunk(
  "webReportEdit/compileReport",
  async (reportId, { rejectWithValue, getState }) => {
    try {
      const { reportContent } = getState().webReportEdit;
      const regenerateSummary = !reportContent?.summary_generated_at;
      const params = new URLSearchParams({
        regenerate_summary: String(regenerateSummary),
      });
      const data = await request(
        `${process.env.REACT_APP_API_URL}/reports/${reportId}/compile/?${params}`,
        { method: "POST" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to compile report");
    }
  }
);

export const createItem = createAsyncThunk(
  "webReportEdit/createItem",
  async ({ type, reportId }, { rejectWithValue }) => {
    const endpoint = ITEM_TYPE_TO_ENDPOINT[type];
    if (!endpoint) {
      return rejectWithValue(`Unsupported item type: ${type}`);
    }
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/${endpoint}/`,
        {
          method: "POST",
          body: JSON.stringify({ report: reportId }),
        }
      );
      return { type, item: data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create item");
    }
  }
);

export const deleteItem = createAsyncThunk(
  "webReportEdit/deleteItem",
  async ({ type, itemId }, { rejectWithValue }) => {
    const endpoint = ITEM_TYPE_TO_ENDPOINT[type];
    if (!endpoint) {
      return rejectWithValue(`Unsupported item type: ${type}`);
    }
    try {
      await request(
        `${process.env.REACT_APP_API_URL}/${endpoint}/${itemId}/`,
        { method: "DELETE" }
      );
      return { type, itemId };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete item");
    }
  }
);

// PATCH editable text fields on an existing item. Caller passes only the
// fields that changed.
export const updateItem = createAsyncThunk(
  "webReportEdit/updateItem",
  async ({ type, itemId, fields }, { rejectWithValue }) => {
    const endpoint = ITEM_TYPE_TO_ENDPOINT[type];
    if (!endpoint) {
      return rejectWithValue(`Unsupported item type: ${type}`);
    }
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/${endpoint}/${itemId}/`,
        {
          method: "PATCH",
          body: JSON.stringify(fields),
        }
      );
      return { type, itemId, item: data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to save changes");
    }
  }
);

// Two-step upload per Image API: POST /image/ then POST /image/{id}/connect/.
// `type` is the slice's capitalized form; the connect API wants singular lowercase.
export const uploadItemImage = createAsyncThunk(
  "webReportEdit/uploadItemImage",
  async ({ type, itemId, file }, { rejectWithValue }) => {
    try {
      const uploadFd = new FormData();
      uploadFd.append("image", file);
      const uploaded = await request(
        `${process.env.REACT_APP_API_URL}/image/`,
        { method: "POST", body: uploadFd }
      );
      const connected = await request(
        `${process.env.REACT_APP_API_URL}/image/${uploaded.id}/connect/`,
        {
          method: "POST",
          body: JSON.stringify({
            model_type: type.toLowerCase(),
            object_id: itemId,
            reverse_relation_name: "images",
          }),
        }
      );
      return { type, itemId, image: connected };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to upload photo");
    }
  }
);

export const deleteItemImage = createAsyncThunk(
  "webReportEdit/deleteItemImage",
  async ({ imageId }, { rejectWithValue }) => {
    try {
      await request(
        `${process.env.REACT_APP_API_URL}/image/${imageId}/`,
        { method: "DELETE" }
      );
      return { imageId };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete photo");
    }
  }
);

// Two-step cover photo update: upload the file via POST /image/, then point
// the report's profile_image at it via PUT /reports/{id}/full-info/.
export const uploadCoverPhoto = createAsyncThunk(
  "webReportEdit/uploadCoverPhoto",
  async ({ reportId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      const uploaded = await request(
        `${process.env.REACT_APP_API_URL}/image/`,
        { method: "POST", body: formData }
      );
      const updated = await request(
        `${process.env.REACT_APP_API_URL}/reports/${reportId}/full-info/`,
        {
          method: "PUT",
          body: JSON.stringify({ profile_image: uploaded.id }),
        }
      );
      // Neither the upload nor the PUT response is guaranteed to carry a
      // presigned_url (it's generated at read time), so fetch the image
      // back if needed — the UI can't render without it.
      if (updated?.profile_image?.presigned_url) {
        return updated.profile_image;
      }
      if (uploaded?.presigned_url) {
        return uploaded;
      }
      return await request(
        `${process.env.REACT_APP_API_URL}/image/${uploaded.id}/`
      );
    } catch (error) {
      return rejectWithValue(error.message || "Failed to upload cover photo");
    }
  }
);

// GET /image/{id}/?include_annotations=true — returns the image with its
// saved annotations attached. Used by the Markup modal to hydrate the canvas
// when reopening an already-annotated photo.
export const fetchImageWithAnnotations = createAsyncThunk(
  "webReportEdit/fetchImageWithAnnotations",
  async (imageId, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ include_annotations: "true" });
      const data = await request(
        `${process.env.REACT_APP_API_URL}/image/${imageId}/?${params}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load annotations");
    }
  }
);

// POST /image/{id}/annotated/ — replace-not-merge. `annotated_image_file` is
// the flattened photo (base + arrows rendered onto a canvas), `annotations`
// is the JSON payload preserving each arrow in image-space so the same
// markup can be reopened and edited later.
export const saveAnnotatedImage = createAsyncThunk(
  "webReportEdit/saveAnnotatedImage",
  async ({ imageId, file, annotations }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("annotated_image_file", file, "annotated.jpg");
      formData.append("annotations", JSON.stringify(annotations));
      const data = await request(
        `${process.env.REACT_APP_API_URL}/image/${imageId}/annotated/`,
        { method: "POST", body: formData }
      );
      return { imageId, data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to save annotations");
    }
  }
);

// Clear all annotations — removes the annotated image so the photo reverts to
// its clean base. Used when the user clears every arrow in the markup editor;
// the POST /annotated/ endpoint requires at least one annotation, so clearing
// goes through DELETE instead.
export const deleteAnnotatedImage = createAsyncThunk(
  "webReportEdit/deleteAnnotatedImage",
  async ({ imageId }, { rejectWithValue }) => {
    try {
      await request(
        `${process.env.REACT_APP_API_URL}/image/${imageId}/annotated/`,
        { method: "DELETE" }
      );
      return { imageId };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to clear annotations");
    }
  }
);

export const aiGenerateItem = createAsyncThunk(
  "webReportEdit/aiGenerateItem",
  async (
    {
      type,
      reportId,
      itemId,
      images,
      annotatedImages,
      annotations,
      descriptionHint,
      concernLevel,
      idempotencyKey,
    },
    { rejectWithValue }
  ) => {
    const endpoint = ITEM_TYPE_TO_ENDPOINT[type];
    if (!endpoint) {
      return rejectWithValue(`Unsupported item type: ${type}`);
    }
    try {
      const formData = new FormData();
      formData.append("report_id", reportId);
      if (itemId) formData.append("existing_item_id", itemId);
      if (descriptionHint) formData.append("description_hint", descriptionHint);
      if (type === "Issue" && concernLevel !== undefined && concernLevel !== null) {
        formData.append("concern_level", String(concernLevel));
      }
      if (idempotencyKey) formData.append("idempotency_key", idempotencyKey);
      // Parallel arrays: annotated_images[i] / annotations[i] map to images[i].
      // The annotated photo is stored separately from its base so it stays
      // editable later (per Async-Item-Generation-API.md).
      (images || []).forEach((file, i) => {
        formData.append("images", file);
        const annotatedFile = annotatedImages?.[i];
        const annotationList = annotations?.[i];
        if (annotatedFile && annotationList?.length) {
          formData.append(`annotated_images[${i}]`, annotatedFile);
          formData.append(`annotations[${i}]`, JSON.stringify(annotationList));
        }
      });

      const data = await request(
        `${process.env.REACT_APP_API_URL}/${endpoint}/async-generate/`,
        { method: "POST", body: formData }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to start AI generation");
    }
  }
);

// Async-update (PATCH) re-runs AI on an existing item. Append one
// `keep_image_ids` form entry per existing image UUID to preserve, plus
// `images` File[] for newly-staged uploads. Backend deletes anything not
// listed in keep_image_ids and adds the new files — keeps existing image
// IDs stable so downstream annotations don't break, and avoids re-fetching
// CORS-blocked presigned URLs.
export const aiUpdateItem = createAsyncThunk(
  "webReportEdit/aiUpdateItem",
  async (
    { type, itemId, keptImageIds = [], newImages = [], descriptionHint, concernLevel },
    { rejectWithValue }
  ) => {
    const endpoint = ITEM_TYPE_TO_ENDPOINT[type];
    if (!endpoint) {
      return rejectWithValue(`Unsupported item type: ${type}`);
    }
    try {
      const formData = new FormData();
      newImages.forEach((file) => formData.append("images", file));
      // One `keep_image_ids` form entry per UUID (DRF list-field convention).
      // Omitted entirely when there's nothing to preserve.
      keptImageIds.forEach((id) => formData.append("keep_image_ids", id));
      if (descriptionHint) formData.append("description_hint", descriptionHint);
      // Non-Issue endpoints reject any concern_level (including "") with 400.
      if (type === "Issue" && concernLevel !== undefined && concernLevel !== null) {
        formData.append("concern_level", String(concernLevel));
      }

      const data = await request(
        `${process.env.REACT_APP_API_URL}/${endpoint}/${itemId}/async-update/`,
        { method: "PATCH", body: formData }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to start AI update");
    }
  }
);

const webReportEditSlice = createSlice({
  name: "webReportEdit",
  initialState,
  reducers: {
    openNewItemModal: (state, action) => {
      state.newItemModal.isOpen = true;
      state.newItemModal.type = action.payload.type;
      state.newItemModal.itemId = action.payload.itemId || null;
    },
    closeNewItemModal: (state) => {
      state.newItemModal.isOpen = false;
      state.newItemModal.type = null;
      state.newItemModal.itemId = null;
      state.errors.createItem = null;
      state.errors.aiGenerate = null;
    },
    addAIProcessingItem: (state, action) => {
      if (
        !state.aiProcessing.items.some((i) => i.taskId === action.payload.taskId)
      ) {
        state.aiProcessing.items.unshift(action.payload);
      }
    },
    updateAIProcessingItem: (state, action) => {
      const { taskId, ...updates } = action.payload;
      const existing = state.aiProcessing.items.find((i) => i.taskId === taskId);
      if (existing) {
        Object.assign(existing, updates);
        // Release blob previews only on SUCCESS. A FAILURE keeps them so the
        // failed (S6) card and widget row can still show the submitted photos
        // and a Retry can replay them. Non-blob entries (kept-image presigned
        // URLs) are unaffected — revokeObjectURL is a no-op on them.
        if (updates.taskState === "SUCCESS" && existing.photoUrls?.length) {
          existing.photoUrls.forEach((url) => {
            try {
              URL.revokeObjectURL(url);
            } catch (_) {
              // ignore
            }
          });
          existing.photoUrls = [];
        }
      }
    },
    dismissAIProcessingItem: (state, action) => {
      const item = state.aiProcessing.items.find(
        (i) => i.taskId === action.payload
      );
      (item?.photoUrls || []).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {
          // ignore
        }
      });
      state.aiProcessing.items = state.aiProcessing.items.filter(
        (i) => i.taskId !== action.payload
      );
    },
    toggleAIProcessingExpanded: (state) => {
      state.aiProcessing.isExpanded = !state.aiProcessing.isExpanded;
    },
    // Discard all failed items — used by the leave guard on a confirmed
    // "Leave" so session-scoped failed cards aren't re-rendered on return.
    clearFailedAIProcessing: (state) => {
      state.aiProcessing.items = state.aiProcessing.items.filter((i) => {
        if (i.taskState !== "FAILURE") return true;
        (i.photoUrls || []).forEach((url) => {
          try {
            URL.revokeObjectURL(url);
          } catch (_) {
            // ignore
          }
        });
        return false;
      });
    },
    // The widget ✕ clears the log row only — it hides the row but never
    // removes the item or its (failed) card. The card is cleared by the
    // item-level Discard/Cancel/Retry, which dispatch dismissAIProcessingItem.
    dismissWidgetRow: (state, action) => {
      const item = state.aiProcessing.items.find(
        (i) => i.taskId === action.payload
      );
      if (item) item.widgetDismissed = true;
    },
    // Retry: re-key a failed entry to the new backend task and put it back into
    // the processing state in place — keeps the same photoUrls (no revoke, they
    // are replayed) so the card/row stay populated while re-processing.
    retryAIProcessingReset: (state, action) => {
      const { oldTaskId, newTaskId, itemId } = action.payload;
      const item = state.aiProcessing.items.find(
        (i) => i.taskId === oldTaskId
      );
      if (!item) return;
      item.taskId = newTaskId;
      if (itemId) item.itemId = itemId;
      item.taskState = "PENDING";
      item.itemStatus = "analyzing";
      item.error = null;
      item.widgetDismissed = false;
    },
    enterEdit: (state, action) => {
      const { itemId, type, draft } = action.payload;
      state.edit.editingTypeById[itemId] = type;
      state.edit.draftById[itemId] = draft || {};
      state.edit.dirtyById[itemId] = false;
      state.errors.updateItem = null;
    },
    updateEditDraft: (state, action) => {
      const { itemId, field, value } = action.payload;
      if (!state.edit.draftById[itemId]) return;
      state.edit.draftById[itemId][field] = value;
    },
    setEditDirty: (state, action) => {
      const { itemId, isDirty } = action.payload;
      // Ignore late updates from a form that has already exited.
      if (!(itemId in state.edit.editingTypeById)) return;
      state.edit.dirtyById[itemId] = isDirty;
    },
    // With an itemId payload: close that one item. Without: close all open
    // items (used by the discard-all navigation guard).
    exitEdit: (state, action) => {
      const itemId = action.payload;
      if (itemId === undefined || itemId === null) {
        state.edit.editingTypeById = {};
        state.edit.draftById = {};
        state.edit.dirtyById = {};
        // The rerun banner is a transient nudge scoped to the edit session it
        // was raised in — drop it on exit so it doesn't reappear on re-enter
        // (AS-905).
        state.pendingAnnotationRerun = {};
      } else {
        delete state.edit.editingTypeById[itemId];
        delete state.edit.draftById[itemId];
        delete state.edit.dirtyById[itemId];
        delete state.pendingAnnotationRerun[itemId];
      }
      state.errors.updateItem = null;
    },
    setPendingAnnotationRerun: (state, action) => {
      state.pendingAnnotationRerun[action.payload] = true;
    },
    clearPendingAnnotationRerun: (state, action) => {
      delete state.pendingAnnotationRerun[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStructuredReport.pending, (state) => {
        state.requestStatus = "loading";
      })
      .addCase(fetchStructuredReport.fulfilled, (state, action) => {
        state.requestStatus = "success";
        state.reportContent = action.payload;
      })
      .addCase(fetchAllSections.fulfilled, (state, action) => {
        state.allSections = action.payload;
      })
      .addCase(fetchStructuredReport.rejected, (state, action) => {
        state.requestStatus = "fail";
        state.error = action.payload;
      });

    builder
      .addCase(createItem.pending, (state) => {
        state.loading.createItem = true;
        state.errors.createItem = null;
      })
      .addCase(createItem.fulfilled, (state, action) => {
        state.loading.createItem = false;
        state.newItemModal.isOpen = true;
        state.newItemModal.type = action.payload.type;
        state.newItemModal.itemId = action.payload.item?.id || null;
      })
      .addCase(createItem.rejected, (state, action) => {
        state.loading.createItem = false;
        state.errors.createItem = action.payload;
      });

    builder
      .addCase(deleteItem.pending, (state) => {
        state.loading.deleteItem = true;
        state.errors.deleteItem = null;
      })
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.loading.deleteItem = false;
        removeItemLocally(state.reportContent, action.payload);
      })
      .addCase(deleteItem.rejected, (state, action) => {
        state.loading.deleteItem = false;
        state.errors.deleteItem = action.payload;
      });

    builder
      .addCase(updateItem.pending, (state) => {
        state.loading.updateItem = true;
        state.errors.updateItem = null;
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        state.loading.updateItem = false;
        // Edit state is intentionally NOT cleared — callers decide (Save
        // exits via exitEdit; Move keeps the user in S3 on the relocated card).
        spliceUpdatedItem(state.reportContent, action.payload, state.allSections);
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.loading.updateItem = false;
        state.errors.updateItem = action.payload;
      });

    builder
      .addCase(aiGenerateItem.pending, (state) => {
        state.loading.aiGenerate = true;
        state.errors.aiGenerate = null;
      })
      .addCase(aiGenerateItem.fulfilled, (state) => {
        state.loading.aiGenerate = false;
      })
      .addCase(aiGenerateItem.rejected, (state, action) => {
        state.loading.aiGenerate = false;
        state.errors.aiGenerate = action.payload;
      });

    // PATCH /async-update/ — reuses the aiGenerate flag for shared spinners.
    builder
      .addCase(aiUpdateItem.pending, (state) => {
        state.loading.aiGenerate = true;
        state.errors.aiGenerate = null;
      })
      .addCase(aiUpdateItem.fulfilled, (state) => {
        state.loading.aiGenerate = false;
      })
      .addCase(aiUpdateItem.rejected, (state, action) => {
        state.loading.aiGenerate = false;
        state.errors.aiGenerate = action.payload;
      });

    builder
      .addCase(compileReport.pending, (state) => {
        state.loading.compileReport = true;
        state.errors.compileReport = null;
      })
      .addCase(compileReport.fulfilled, (state) => {
        state.loading.compileReport = false;
      })
      .addCase(compileReport.rejected, (state, action) => {
        state.loading.compileReport = false;
        state.errors.compileReport = action.payload;
      });

    // Photo CRUD splices reportContent — no /structured/ refetch needed.
    builder
      .addCase(uploadItemImage.fulfilled, (state, action) => {
        const { itemId, image } = action.payload;
        forEachItem(state.reportContent, (it) => {
          if (it.id !== itemId) return;
          it.images = [...(it.images || []), image];
        });
      })
      .addCase(deleteItemImage.fulfilled, (state, action) => {
        const { imageId } = action.payload;
        forEachItem(state.reportContent, (it) => {
          if (!it.images?.length) return;
          it.images = it.images.filter((img) => img.id !== imageId);
        });
      })
      .addCase(saveAnnotatedImage.fulfilled, (state, action) => {
        const { data } = action.payload;
        const baseId = data?.base_image_id;
        if (!baseId) return;
        // Strip the redundant nested `base_image` — the splice target IS the
        // base image, so we only need the annotated layer's fields.
        const { base_image: _b, ...annotated } = data;
        forEachItem(state.reportContent, (it) => {
          if (!it.images?.length) return;
          const owns = it.images.some((img) => img.id === baseId);
          if (!owns) return;
          it.images = it.images.map((img) =>
            img.id === baseId ? { ...img, annotated_image: annotated } : img
          );
          // Flag the parent item so the indigo banner prompts a re-analyze.
          state.pendingAnnotationRerun[it.id] = true;
        });
      })
      .addCase(deleteAnnotatedImage.fulfilled, (state, action) => {
        const { imageId } = action.payload;
        forEachItem(state.reportContent, (it) => {
          if (!it.images?.length) return;
          const owns = it.images.some((img) => img.id === imageId);
          if (!owns) return;
          it.images = it.images.map((img) =>
            img.id === imageId ? { ...img, annotated_image: null } : img
          );
          // The analyzed photo changed — prompt a re-analyze, same as saving.
          state.pendingAnnotationRerun[it.id] = true;
        });
      });

    builder
      .addCase(uploadCoverPhoto.pending, (state) => {
        state.loading.uploadCoverPhoto = true;
        state.errors.uploadCoverPhoto = null;
      })
      .addCase(uploadCoverPhoto.fulfilled, (state, action) => {
        state.loading.uploadCoverPhoto = false;
        if (state.reportContent) {
          state.reportContent.profile_image = action.payload;
        }
      })
      .addCase(uploadCoverPhoto.rejected, (state, action) => {
        state.loading.uploadCoverPhoto = false;
        state.errors.uploadCoverPhoto = action.payload;
      });
  },
});

function forEachItem(reportContent, cb) {
  for (const section of reportContent?.sections || []) {
    for (const overview of section.overviews || []) cb(overview);
    for (const sub of section.subsections || []) {
      for (const list of [sub.issues, sub.infos, sub.limits]) {
        for (const item of list || []) cb(item);
      }
    }
  }
}

// Applies a PATCH-response item back into reportContent — handles both
// in-place updates and section/subsection moves.
const TYPE_TO_LIST_KEY = {
  Issue: "issues",
  Info: "infos",
  Limit: "limits",
  Overview: "overviews",
};

function spliceUpdatedItem(reportContent, payload, allSections) {
  const { type, item } = payload || {};
  const listKey = TYPE_TO_LIST_KEY[type];
  if (!reportContent?.sections || !item || !listKey) return;

  if (type === "Overview") {
    let sourceSectionName = null;
    for (const section of reportContent.sections) {
      const list = section.overviews;
      if (!list) continue;
      const idx = list.findIndex((i) => i.id === item.id);
      if (idx === -1) continue;
      if (section.name === item.section) {
        list[idx] = item;
        return;
      }
      list.splice(idx, 1);
      sourceSectionName = section.name;
      break;
    }
    // Create the destination section if it had no items yet (so a move into a
    // previously-empty template section still renders the item).
    const target = ensureSection(reportContent, allSections, item.section);
    if (!target.overviews) target.overviews = [];
    target.overviews.push(item);
    // Drop the source section if the move left it empty.
    if (sourceSectionName) pruneIfEmpty(reportContent, sourceSectionName, null);
    return;
  }

  // Issue / Info / Limit — find by id across all subsections.
  for (const section of reportContent.sections) {
    for (const sub of section.subsections || []) {
      const list = sub[listKey];
      if (!list) continue;
      const idx = list.findIndex((i) => i.id === item.id);
      if (idx === -1) continue;
      if (section.name === item.section && sub.name === item.subsection) {
        list[idx] = item;
        return;
      }
      list.splice(idx, 1);
      recountSub(sub);
      const sourceSectionName = section.name;
      const sourceSubName = sub.name;
      // Scaffold the destination section/subsection when missing so a move
      // into a previously-empty template location still renders the item.
      const target = ensureSection(reportContent, allSections, item.section);
      const targetSub = ensureSubsection(
        target,
        allSections,
        item.section,
        item.subsection
      );
      if (!targetSub[listKey]) targetSub[listKey] = [];
      targetSub[listKey].push(item);
      recountSub(targetSub);
      // Drop the source subsection/section if the move left them empty.
      pruneIfEmpty(reportContent, sourceSectionName, sourceSubName);
      return;
    }
  }
}

// After a move, drop the source subsection and/or section when relocating the
// item left them empty. Re-finds by name so it's safe against index shifts
// from any destination section scaffolded earlier in the same update.
function pruneIfEmpty(reportContent, sectionName, subName) {
  const sectionIdx = reportContent.sections.findIndex(
    (s) => s.name === sectionName
  );
  if (sectionIdx === -1) return;
  const section = reportContent.sections[sectionIdx];
  if (subName != null) {
    const subs = section.subsections || [];
    const subIdx = subs.findIndex((s) => s.name === subName);
    if (subIdx !== -1 && isSubEmpty(subs[subIdx])) {
      subs.splice(subIdx, 1);
    }
  }
  cleanupEmptySection(reportContent, sectionIdx);
}

// Insert `entry` into `list` at the position implied by `orderedNames`
// (template order), so a scaffolded section/subsection lands where the
// template expects rather than always at the end. Appends when the name is
// absent from the template ordering.
function insertByTemplateOrder(list, entry, orderedNames, name) {
  const rank = orderedNames.indexOf(name);
  if (rank === -1) {
    list.push(entry);
    return;
  }
  const insertAt = list.findIndex(
    (existing) => orderedNames.indexOf(existing.name) > rank
  );
  if (insertAt === -1) list.push(entry);
  else list.splice(insertAt, 0, entry);
}

// Return the named section, creating an empty one (in template order) when the
// report has no items there yet.
function ensureSection(reportContent, allSections, sectionName) {
  const existing = reportContent.sections.find((s) => s.name === sectionName);
  if (existing) return existing;
  const section = { name: sectionName, overviews: [], subsections: [] };
  insertByTemplateOrder(
    reportContent.sections,
    section,
    (allSections || []).map((s) => s.name),
    sectionName
  );
  return section;
}

// Return the named subsection within `section`, creating an empty one (in
// template order) when absent.
function ensureSubsection(section, allSections, sectionName, subName) {
  if (!section.subsections) section.subsections = [];
  const existing = section.subsections.find((s) => s.name === subName);
  if (existing) return existing;
  const sub = { name: subName, issues: [], infos: [], limits: [] };
  recountSub(sub);
  const tmplSection = (allSections || []).find((s) => s.name === sectionName);
  insertByTemplateOrder(
    section.subsections,
    sub,
    (tmplSection?.subsections || []).map((s) => s.name),
    subName
  );
  return sub;
}

function recountSub(sub) {
  sub.issue_count = (sub.issues || []).length;
  sub.info_count = (sub.infos || []).length;
  sub.limit_count = (sub.limits || []).length;
}

// Cascades: an empty subsection is removed; a section with no subsections
// AND no overviews is removed too.
function removeItemLocally(reportContent, payload) {
  const { type, itemId } = payload || {};
  const listKey = TYPE_TO_LIST_KEY[type];
  if (!reportContent?.sections || !listKey || itemId == null) return;

  if (type === "Overview") {
    for (let s = 0; s < reportContent.sections.length; s += 1) {
      const section = reportContent.sections[s];
      const list = section.overviews;
      if (!list) continue;
      const idx = list.findIndex((i) => i.id === itemId);
      if (idx !== -1) {
        list.splice(idx, 1);
        cleanupEmptySection(reportContent, s);
        return;
      }
    }
    return;
  }

  for (let s = 0; s < reportContent.sections.length; s += 1) {
    const section = reportContent.sections[s];
    const subs = section.subsections || [];
    for (let sb = 0; sb < subs.length; sb += 1) {
      const sub = subs[sb];
      const list = sub[listKey];
      if (!list) continue;
      const idx = list.findIndex((i) => i.id === itemId);
      if (idx !== -1) {
        list.splice(idx, 1);
        recountSub(sub);
        if (isSubEmpty(sub)) {
          subs.splice(sb, 1);
          cleanupEmptySection(reportContent, s);
        }
        return;
      }
    }
  }
}

function isSubEmpty(sub) {
  return (
    (sub.issues || []).length === 0 &&
    (sub.infos || []).length === 0 &&
    (sub.limits || []).length === 0
  );
}

function cleanupEmptySection(reportContent, sectionIdx) {
  const section = reportContent.sections[sectionIdx];
  if (!section) return;
  const hasSubs = (section.subsections || []).length > 0;
  const hasOverviews = (section.overviews || []).length > 0;
  if (!hasSubs && !hasOverviews) {
    reportContent.sections.splice(sectionIdx, 1);
  }
}

export const {
  openNewItemModal,
  closeNewItemModal,
  addAIProcessingItem,
  updateAIProcessingItem,
  dismissAIProcessingItem,
  dismissWidgetRow,
  clearFailedAIProcessing,
  retryAIProcessingReset,
  toggleAIProcessingExpanded,
  enterEdit,
  updateEditDraft,
  setEditDirty,
  exitEdit,
  setPendingAnnotationRerun,
  clearPendingAnnotationRerun,
} = webReportEditSlice.actions;

// Polls GET /tasks/{taskId}/status/ until terminal. 404 means PENDING (keep
// polling). On SUCCESS, refetches /structured/ so the new item appears.
export const pollTaskStatus =
  ({ taskId, reportId }) =>
  async (dispatch) => {
    // TEST-ONLY: force the AI task to fail so the S6 failed-item retry UI can
    // be exercised without a real backend failure. Toggle in devtools console:
    //   localStorage.setItem("webeditMockAiFail", "1")  // next task(s) fail
    //   localStorage.removeItem("webeditMockAiFail")     // off → Retry succeeds
    // Remove this block before release.
    let mockFail = false;
    try {
      mockFail = window.localStorage.getItem("webeditMockAiFail") === "1";
    } catch (_) {
      mockFail = false;
    }
    if (mockFail) {
      dispatch(
        updateAIProcessingItem({
          taskId,
          taskState: "FAILURE",
          itemStatus: "failed",
          error: "Mocked failure (test hook)",
        })
      );
      return;
    }

    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      if (attempts > POLL_MAX_ATTEMPTS) {
        dispatch(
          updateAIProcessingItem({
            taskId,
            taskState: "FAILURE",
            itemStatus: "timed-out",
            error: "Polling timed out",
          })
        );
        return;
      }

      try {
        const data = await request(
          `${process.env.REACT_APP_API_URL}/tasks/${taskId}/status/`
        );
        dispatch(
          updateAIProcessingItem({
            taskId,
            taskState: data.task_state,
            itemStatus: data.item_status,
            error: data.error || null,
          })
        );

        if (TERMINAL_TASK_STATES.has(data.task_state)) {
          if (data.task_state === "SUCCESS" && reportId) {
            dispatch(fetchStructuredReport(reportId));
          }
          return;
        }
      } catch (err) {
        // 404 / transient errors → keep polling. Terminal failures surface
        // via task_state in the success branch above.
      }

      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
  };

// Retry a failed AI item by replaying its original submission (registered at
// submit time). Re-fires the same thunk against the existing item, re-keys the
// failed entry back into processing in place, and resumes polling. Returns
// { ok } so the caller can toast on failure to start.
export const retryAIProcessingItem =
  ({ taskId }) =>
  async (dispatch) => {
    const payload = getRetryPayload(taskId);
    if (!payload) return { ok: false };
    const { kind, args, reportId } = payload;
    try {
      const data =
        kind === "create"
          ? await dispatch(aiGenerateItem(args)).unwrap()
          : await dispatch(aiUpdateItem(args)).unwrap();
      const newTaskId = data.task_id;
      const itemId = data.item_id || args.itemId;
      dispatch(retryAIProcessingReset({ oldTaskId: taskId, newTaskId, itemId }));
      clearRetryPayload(taskId);
      registerRetryPayload(newTaskId, {
        kind,
        args: { ...args, itemId },
        reportId,
      });
      dispatch(pollTaskStatus({ taskId: newTaskId, reportId }));
      return { ok: true };
    } catch (_err) {
      return { ok: false };
    }
  };

export default webReportEditSlice.reducer;
